import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import { PageHeader, Card, CardBody, Button, Input, Select, Empty, Badge } from "../../components/admin/UI";
import { ClipboardCheck, Save } from "lucide-react";
import { toast } from "sonner";
import { downloadXlsx } from "../../lib/download";

const STATUS_OPTIONS = [
    { v: "present", l: "Présent", color: "emerald" },
    { v: "absent", l: "Absent", color: "red" },
    { v: "retard", l: "Retard", color: "amber" },
];

export default function Attendance() {
    const { activeClubId, activeSeasonId, canManageSports } = useAuth();
    const [teams, setTeams] = useState([]);
    const [teamId, setTeamId] = useState("");
    const [players, setPlayers] = useState([]);
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [marks, setMarks] = useState({}); // player_id -> status
    const [teamStats, setTeamStats] = useState([]);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        if (!activeClubId) return;
        const params = { club_id: activeClubId };
        if (activeSeasonId) params.season_id = activeSeasonId;
        api.get("/teams", { params }).then((r) => {
            setTeams(r.data);
            if (r.data[0]) setTeamId((cur) => cur || r.data[0].id);
        });
    }, [activeClubId, activeSeasonId]);

    useEffect(() => {
        if (!activeClubId || !teamId) return;
        api.get("/players", { params: { club_id: activeClubId, team_id: teamId, season_id: activeSeasonId } }).then((r) => setPlayers(r.data));
        api.get("/attendance", { params: { club_id: activeClubId, team_id: teamId, season_id: activeSeasonId, date } }).then((r) => {
            const m = {};
            r.data.forEach((a) => { m[a.player_id] = a.status; });
            setMarks(m);
        });
        api.get(`/attendance/stats/team/${teamId}`, { params: { club_id: activeClubId, season_id: activeSeasonId } }).then((r) => setTeamStats(r.data)).catch(() => setTeamStats([]));
    }, [activeClubId, activeSeasonId, teamId, date]);

    const setStatus = (pid, s) => setMarks((m) => ({ ...m, [pid]: s }));
    const allPresent = () => { const m = {}; players.forEach((p) => (m[p.id] = "present")); setMarks(m); };

    const submit = async () => {
        if (!activeSeasonId) { toast.error("Sélectionnez une saison"); return; }
        const entries = players.map((p) => ({ player_id: p.id, status: marks[p.id] || "absent" }));
        try {
            await api.post("/attendance/bulk", { team_id: teamId, season_id: activeSeasonId, date, entries }, { params: { club_id: activeClubId } });
            toast.success("Présences enregistrées");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const exportAttendance = async () => {
        if (!activeSeasonId) { toast.error("Sélectionnez une saison"); return; }
        setExporting(true);
        try {
            await downloadXlsx("/exports/attendance", {
                club_id: activeClubId, season_id: activeSeasonId, team_id: teamId, date,
            }, "presences.xlsx");
            toast.success("Export Excel téléchargé");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setExporting(false);
        }
    };

    const statByPlayer = useMemo(() => {
        const m = {};
        teamStats.forEach((s) => { m[s.player_id] = s; });
        return m;
    }, [teamStats]);

    return (
        <div data-testid="attendance-page">
            <PageHeader title="Présences" subtitle="Marquez les présences aux entraînements et suivez les taux par joueur"
                action={canManageSports && <Button variant="secondary" onClick={exportAttendance} disabled={exporting || !activeSeasonId || !teamId}>
                    {exporting ? "Export…" : "Exporter Excel"}
                </Button>} />

            <Card className="mb-6">
                <CardBody>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <Select label="Équipe" value={teamId} onChange={(e) => setTeamId(e.target.value)} data-testid="attendance-team-select">
                            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </Select>
                        <Input label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="attendance-date-input" />
                        <div className="flex items-end gap-2">
                            <Button variant="secondary" onClick={allPresent} data-testid="all-present-btn">Tous présents</Button>
                            <Button onClick={submit} data-testid="attendance-save"><Save className="w-4 h-4" /> Enregistrer</Button>
                        </div>
                    </div>
                </CardBody>
            </Card>

            {players.length === 0 ? (
                <Empty title="Aucun joueur" hint="Ajoutez des joueurs à l'équipe sélectionnée." />
            ) : (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                                <tr className="text-left">
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Joueur</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Statut</th>
                                    <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">Taux saison</th>
                                </tr>
                            </thead>
                            <tbody>
                                {players.map((p) => {
                                    const s = statByPlayer[p.id];
                                    return (
                                        <tr key={p.id} className="border-t border-slate-100" data-testid={`attendance-row-${p.id}`}>
                                            <td className="px-4 py-3 font-semibold text-slate-900">{p.last_name?.toUpperCase()} {p.first_name}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-1">
                                                    {STATUS_OPTIONS.map((opt) => {
                                                        const active = marks[p.id] === opt.v;
                                                        const cls = active
                                                            ? `bg-${opt.color}-500 text-white border-${opt.color}-500`
                                                            : `bg-white text-slate-700 border-slate-200 hover:border-slate-300`;
                                                        return (
                                                            <button
                                                                key={opt.v}
                                                                onClick={() => setStatus(p.id, opt.v)}
                                                                className={`px-2.5 py-1 text-xs rounded-md border font-medium transition-colors ${active ? "" : cls} ${active && opt.v === "present" ? "bg-emerald-500 text-white border-emerald-500" : ""} ${active && opt.v === "absent" ? "bg-red-500 text-white border-red-500" : ""} ${active && opt.v === "retard" ? "bg-amber-500 text-white border-amber-500" : ""}`}
                                                                data-testid={`attendance-${p.id}-${opt.v}`}
                                                            >
                                                                {opt.l}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                {s ? (
                                                    <div className="flex items-center gap-2">
                                                        <Badge color={s.attendance_rate >= 80 ? "emerald" : s.attendance_rate >= 50 ? "amber" : "red"}>
                                                            {s.attendance_rate}%
                                                        </Badge>
                                                        <span className="text-xs text-slate-500">{s.present}P · {s.absent}A · {s.retard}R · {s.total}T</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-slate-400">Aucune donnée</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </div>
    );
}
